import React from 'react';
import { Users, Target, Award, TrendingUp, Heart, Shield, Clock, Zap } from 'lucide-react';
import Header from './components/Header19';
import Footer from './components/Footer19';

const About = () => {
    return (
        <div className="w-full min-h-screen bg-white font-sans">
            <Header />

            <main className="w-full">
                {/* Hero Section */}
                <section className="relative w-full min-h-[50vh] lg:min-h-[60vh] overflow-hidden bg-gray-50">
                    <img
                        src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&h=1080"
                        alt="About Us Hero"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
                    <div className="relative z-10 h-full min-h-[50vh] lg:min-h-[60vh] flex items-center px-3 sm:px-4 lg:px-12">
                        <div className="max-w-3xl">
                            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4 lg:mb-6 leading-tight">About Our Brand</h1>
                            <p className="text-lg lg:text-xl text-white/90 leading-relaxed max-w-2xl">
                                Crafting fashion with passion, innovation, and a commitment to excellence since our founding.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Company Story */}
                <section className="w-full py-12 lg:py-20 px-3 sm:px-4 lg:px-12 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                            <div>
                                <img
                                    src="https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=800&h=600"
                                    alt="Our Story"
                                    className="w-full h-auto rounded-2xl shadow-lg"
                                />
                            </div>
                            <div>
                                <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">Our Story</h2>
                                <div className="space-y-4 text-base lg:text-lg text-gray-600 leading-relaxed">
                                    <p>
                                        Founded with a vision to revolutionize the fashion industry, our journey began with a simple belief:
                                        that style should be accessible, sustainable, and empowering.
                                    </p>
                                    <p>
                                        From our humble beginnings to becoming a recognized name in fashion, we've stayed true to our core values
                                        of quality, creativity, and customer satisfaction.
                                    </p>
                                    <p>
                                        Today, we continue to push boundaries and set new standards in the industry, creating pieces that
                                        inspire confidence and celebrate individuality.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Mission & Vision */}
                <section className="w-full py-12 lg:py-20 px-3 sm:px-4 lg:px-12 bg-gray-50">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12 lg:mb-16">
                            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">Mission & Vision</h2>
                            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                                Guided by purpose, driven by passion
                            </p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-10 hover:shadow-xl transition-all duration-300">
                                <div className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center mb-6">
                                    <Target className="w-7 h-7 text-white" />
                                </div>
                                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">Our Mission</h3>
                                <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                    To create exceptional fashion that empowers individuals to express their unique style while maintaining
                                    the highest standards of quality and sustainability. We strive to make fashion accessible and meaningful
                                    for everyone.
                                </p>
                            </div>
                            <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-10 hover:shadow-xl transition-all duration-300">
                                <div className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center mb-6">
                                    <Zap className="w-7 h-7 text-white" />
                                </div>
                                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
                                <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                    To be the leading fashion brand recognized globally for innovation, sustainability, and customer-centric
                                    approach. We envision a future where style and responsibility go hand in hand, creating positive impact
                                    in communities worldwide.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Core Values */}
                <section className="w-full py-12 lg:py-20 px-3 sm:px-4 lg:px-12 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12 lg:mb-16">
                            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">Our Core Values</h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                The principles that guide everything we do
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                            {[
                                { icon: Heart, title: 'Passion', desc: 'We pour our heart into every design, creating pieces with genuine love and dedication' },
                                { icon: Shield, title: 'Quality', desc: 'Uncompromising standards in materials, craftsmanship, and customer service' },
                                { icon: Users, title: 'Community', desc: 'Building lasting relationships with our customers, partners, and team members' },
                                { icon: TrendingUp, title: 'Innovation', desc: 'Continuously evolving and pushing creative boundaries in fashion' }
                            ].map((value, idx) => (
                                <div key={idx} className="bg-gray-50 rounded-2xl p-6 lg:p-8 hover:shadow-lg hover:bg-white transition-all duration-300 border border-gray-100">
                                    <value.icon className="w-12 h-12 text-gray-900 mb-4" />
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                                    <p className="text-base text-gray-600 leading-relaxed">{value.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Company Stats */}
                <section className="w-full py-12 lg:py-20 px-3 sm:px-4 lg:px-12 bg-gray-50">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12 lg:mb-16">
                            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">Our Impact</h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                Numbers that reflect our commitment and growth
                            </p>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                            {[
                                { num: '50K+', label: 'Happy Customers', sublabel: 'Worldwide' },
                                { num: '500+', label: 'Products Sold', sublabel: 'Monthly' },
                                { num: '99%', label: 'Satisfaction Rate', sublabel: 'Customer Reviews' },
                                { num: '15+', label: 'Years Experience', sublabel: 'In Fashion Industry' }
                            ].map((stat, idx) => (
                                <div key={idx} className="bg-white rounded-2xl p-6 lg:p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300">
                                    <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">{stat.num}</div>
                                    <div className="text-base lg:text-lg font-semibold text-gray-700 mb-1">{stat.label}</div>
                                    <div className="text-sm text-gray-500">{stat.sublabel}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Our Team Section */}
                <section className="w-full py-12 lg:py-20 px-3 sm:px-4 lg:px-12 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12 lg:mb-16">
                            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                The talented people behind our success
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                { name: 'Founder & CEO', role: 'Visionary Leader', img: 'photo-1494790108377-be9c29b29330' },
                                { name: 'Creative Director', role: 'Design Expert', img: 'photo-1507003211169-0a1dd7228f2d' },
                                { name: 'Head of Production', role: 'Operations Lead', img: 'photo-1573497019940-1c28c88b4f3e' },
                                { name: 'Marketing Director', role: 'Brand Strategist', img: 'photo-1560250097-0b93528c311a' }
                            ].map((member, idx) => (
                                <div key={idx} className="group">
                                    <div className="relative overflow-hidden rounded-2xl shadow-lg mb-4 aspect-square">
                                        <img
                                            src={`https://images.unsplash.com/${member.img}?w=400&h=400&fit=crop`}
                                            alt={member.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                                    <p className="text-base text-gray-600">{member.role}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Timeline */}
                <section className="w-full py-12 lg:py-20 px-3 sm:px-4 lg:px-12 bg-gray-50">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-12 lg:mb-16">
                            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">Our Journey</h2>
                            <p className="text-lg text-gray-600">
                                Key milestones in our story
                            </p>
                        </div>
                        <div className="space-y-8">
                            {[
                                { year: '2010', title: 'The Beginning', desc: 'Started with a small workshop and big dreams of changing the fashion landscape' },
                                { year: '2013', title: 'First Collection', desc: 'Launched our debut collection, receiving overwhelming response from fashion enthusiasts' },
                                { year: '2016', title: 'Going Global', desc: 'Expanded internationally, bringing our designs to customers across 10 countries' },
                                { year: '2019', title: 'Sustainability Focus', desc: 'Committed to eco-friendly practices, introducing our sustainable fashion line' },
                                { year: '2022', title: 'Digital Innovation', desc: 'Launched innovative online platform, making fashion more accessible than ever' },
                                { year: '2025', title: 'Industry Leader', desc: 'Recognized as a leading brand in fashion innovation and customer satisfaction' }
                            ].map((milestone, idx) => (
                                <div key={idx} className="flex gap-6 lg:gap-8 group">
                                    <div className="flex-shrink-0 w-24 lg:w-32">
                                        <div className="text-2xl lg:text-3xl font-bold text-gray-900">{milestone.year}</div>
                                    </div>
                                    <div className="flex-1 bg-white rounded-2xl p-6 lg:p-8 shadow-lg group-hover:shadow-xl transition-all duration-300">
                                        <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">{milestone.title}</h3>
                                        <p className="text-base lg:text-lg text-gray-600 leading-relaxed">{milestone.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Why Choose Us */}
                <section className="w-full py-12 lg:py-20 px-3 sm:px-4 lg:px-12 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12 lg:mb-16">
                            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                What sets us apart from the rest
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[
                                { icon: Award, title: 'Premium Quality', desc: 'We use only the finest materials and maintain strict quality control at every step of production' },
                                { icon: Clock, title: 'Fast Delivery', desc: 'Efficient logistics ensure your orders reach you quickly, anywhere in the world' },
                                { icon: Shield, title: 'Secure Shopping', desc: 'Your data and transactions are protected with industry-leading security measures' },
                                { icon: Heart, title: 'Customer Care', desc: '24/7 support team ready to assist with any questions or concerns you may have' },
                                { icon: TrendingUp, title: 'Latest Trends', desc: 'Stay ahead with our constantly updated collections featuring the newest fashion trends' },
                                { icon: Users, title: 'Community', desc: 'Join thousands of satisfied customers who trust us for their fashion needs' }
                            ].map((feature, idx) => (
                                <div key={idx} className="bg-gray-50 rounded-2xl p-6 lg:p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-100">
                                    <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center mb-4">
                                        <feature.icon className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                                    <p className="text-base text-gray-600 leading-relaxed">{feature.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Company Culture */}
                <section className="w-full py-12 lg:py-20 px-3 sm:px-4 lg:px-12 bg-gray-50">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                            <div className="order-2 lg:order-1">
                                <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">Our Culture</h2>
                                <div className="space-y-4 text-base lg:text-lg text-gray-600 leading-relaxed">
                                    <p>
                                        We believe in creating an environment where creativity flourishes and innovation thrives. Our team
                                        is our greatest asset, and we foster a culture of collaboration, respect, and continuous learning.
                                    </p>
                                    <p>
                                        Work-life balance, professional growth, and personal well-being are not just buzzwords for us—they're
                                        fundamental principles that guide how we operate every day.
                                    </p>
                                    <p>
                                        From sustainable practices to community initiatives, we're committed to making a positive impact
                                        that extends beyond fashion.
                                    </p>
                                </div>
                                <div className="mt-8 grid grid-cols-2 gap-4">
                                    <div className="bg-white rounded-xl p-6 shadow-lg">
                                        <div className="text-2xl font-bold text-gray-900 mb-1">100%</div>
                                        <div className="text-sm text-gray-600">Employee Satisfaction</div>
                                    </div>
                                    <div className="bg-white rounded-xl p-6 shadow-lg">
                                        <div className="text-2xl font-bold text-gray-900 mb-1">50+</div>
                                        <div className="text-sm text-gray-600">Team Members</div>
                                    </div>
                                </div>
                            </div>
                            <div className="order-1 lg:order-2">
                                <div className="grid grid-cols-2 gap-4">
                                    <img
                                        src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=400"
                                        alt="Team Culture 1"
                                        className="w-full h-64 object-cover rounded-2xl shadow-lg"
                                    />
                                    <img
                                        src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&h=400"
                                        alt="Team Culture 2"
                                        className="w-full h-64 object-cover rounded-2xl shadow-lg mt-8"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="w-full py-16 lg:py-24 px-3 sm:px-4 lg:px-12 bg-gray-900">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">Join Our Fashion Journey</h2>
                        <p className="text-lg lg:text-xl text-white/80 mb-8 leading-relaxed">
                            Discover our latest collections and be part of a community that celebrates style, quality, and individuality.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-base hover:bg-gray-100 transition-all duration-300 shadow-xl">
                                Shop Now
                            </button>
                            <button className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-base hover:bg-white hover:text-gray-900 transition-all duration-300">
                                Contact Us
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default About;
